import { useContext, useState } from "react";
import { FiDownload } from "react-icons/fi";
import { UserContext } from "../../Context";
import { USER_ROLES } from "../../Constants/Strings";
import { COMPANY_FIELDS } from "../UserApprove/constant";
import { useIRCompliance } from "./Hooks/useIRCompliance";

// Registers are prepared after the month closes, so default to last month
const getPreviousMonth = () => {
  const now = new Date();
  const previous = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  return `${previous.getFullYear()}-${String(previous.getMonth() + 1).padStart(2, "0")}`;
};

// Roles that can see every user's salary slips and download the reports
// (Admin only sees their own salary data)
export const REPORT_ROLES = [
  USER_ROLES.HR,
  USER_ROLES.DIRECTOR_MANAGEMENT,
  USER_ROLES.ACCOUNTS,
];

const REGISTER_GROUPS = [
  {
    group: "IR Registers",
    options: [
      { type: "formB", label: "Form B - Wage Register", description: "Monthly wages, deductions and employer share per employee (Excel)." },
      { type: "formD", label: "Form D - Attendance Register", description: "Day-wise attendance of every employee for the month (Excel)." },
    ],
  },
  {
    group: "Accounts",
    options: [
      { type: "financialReport", label: "Financial Report", description: "Accounts salary sheet with statutory payables (Excel)." },
    ],
  },
  {
    group: "Statutory Uploads",
    options: [
      { type: "pfEcr", label: "PF ECR - EPFO Upload", description: "ECR text file for the EPFO portal, PF members only (.txt)." },
      { type: "esicReport", label: "ESIC - Monthly Contribution", description: "Contribution upload for the ESIC portal, ESIC-covered employees only (Excel)." },
    ],
  },
];

const REGISTER_OPTIONS = REGISTER_GROUPS.flatMap(({ options }) => options);

const fieldClassName =
  "w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-1 focus:ring-primary focus:outline-none";

const IRComplianceModal = ({ onClose }) => {
  const { user } = useContext(UserContext);
  const { downloading, downloadRegister } = useIRCompliance();
  const [register, setRegister] = useState(REGISTER_OPTIONS[0].type);
  const [month, setMonth] = useState(getPreviousMonth());
  const [company, setCompany] = useState(user?.company || COMPANY_FIELDS[0].value);
  const selected = REGISTER_OPTIONS.find((option) => option.type === register);

  return (
    <div className="fixed inset-0 z-50 bg-black bg-opacity-40 flex items-center justify-center p-4">
      <div className="bg-white w-full max-w-lg mx-4 rounded-2xl shadow-xl p-6 relative">
        <h3 className="text-lg font-semibold text-primary mb-1">IR Compliance Reports</h3>
        <p className="text-xs text-gray-500 mb-4">
          Built from the salary slips of the selected month. Run payroll or upload
          salary slips for the month first.
        </p>

        <div className="flex flex-col gap-4">
          <div className="flex flex-col">
            <label className="text-sm font-medium text-gray-700 mb-1">Report</label>
            <select
              value={register}
              onChange={(e) => setRegister(e.target.value)}
              className={fieldClassName}
            >
              {REGISTER_GROUPS.map(({ group, options }) => (
                <optgroup key={group} label={group}>
                  {options.map(({ type, label }) => (
                    <option key={type} value={type}>
                      {label}
                    </option>
                  ))}
                </optgroup>
              ))}
            </select>
            <p className="text-xs text-gray-500 mt-1">{selected?.description}</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="flex flex-col">
              <label className="text-sm font-medium text-gray-700 mb-1">Month</label>
              <input
                type="month"
                value={month}
                onChange={(e) => setMonth(e.target.value)}
                className={fieldClassName}
              />
            </div>

            <div className="flex flex-col">
              <label className="text-sm font-medium text-gray-700 mb-1">Establishment</label>
              <select
                value={company}
                onChange={(e) => setCompany(e.target.value)}
                className={fieldClassName}
              >
                {COMPANY_FIELDS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-2 mt-5">
          <button
            className="px-4 py-2 rounded-lg bg-gray-200 hover:bg-gray-300 text-sm text-gray-800"
            onClick={onClose}
          >
            Close
          </button>
          <button
            className="px-4 py-2 rounded-lg bg-primary hover:bg-green-600 text-white text-sm flex items-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
            onClick={() => downloadRegister(register, { month, company })}
            disabled={!month || Boolean(downloading)}
          >
            <FiDownload />
            {downloading ? "Generating..." : "Download"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default IRComplianceModal;
